import classNames from 'classnames';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import { Table, TableProps } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';
import { t } from 'i18next';
import { DataNotFound } from 'base/DataNotFound/DataNotFound';

interface AdvanceTableProps {
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  tableProps?: TableProps;
  hasFooter?: boolean;
  showModal?: boolean;
  dataLength?: number;
}

const AdvanceTable = ({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  hasFooter,
  dataLength = 1
}: AdvanceTableProps) => {
  const table = useAdvanceTableContext();
  const { getRowModel, getFlatHeaders, getFooterGroups } = table;

  return (
    <div className=" ms-n1 ps-1">
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr className="border-bottom border-black">
            {getFlatHeaders().map((header, index) => {
              return (
                <th
                  key={`table-header-${header.id}-${index}`}
                  {...header.column.columnDef.meta?.headerProps}
                  className={classNames(
                    header.column.columnDef.meta?.headerProps?.className,
                    {
                      sort: header.column.getCanSort(),
                      desc: header.column.getIsSorted() === 'desc',
                      asc: header.column.getIsSorted() === 'asc'
                    }
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={bodyClassName}>
          {dataLength > 0 ? (
            getRowModel().rows.map((row, index) => (
              <tr
                key={`table-body-${row.id}-${index}`}
                className={`${rowClassName} ${
                  index === getRowModel().rows.length - 1
                    ? 'border-bottom border-dark'
                    : ''
                }`}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    key={`table-cell-${cell.id}-${index}`}
                    {...cell.column.columnDef.meta?.cellProps}
                    className="fw-semibold fs-15 p-2"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={getFlatHeaders().length}
                className="text-center fw-bold fs-6"
              >
                <DataNotFound
                  fullHeight
                  showAsCard={true}
                  description={[
                    t('general.no_data_des_1'),
                    t('general.no_data_des_2')
                  ]}
                />
              </td>
            </tr>
          )}
        </tbody>
        {hasFooter && (
          <tfoot>
            {getFooterGroups().map((footerGroup, foolterIndex) => (
              <tr
                key={`table-footer-row-${footerGroup.id}-${foolterIndex}`}
                className="border-0 border-translucent"
              >
                {footerGroup.headers.map((header, footerHeaderIndex) => {
                  return (
                    <th
                      key={`table-footer-cell-${header.id}-${footerHeaderIndex}`}
                      {...header.column.columnDef.meta?.footerProps}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default AdvanceTable;
